import axios from 'axios'
import config from './config.js'

// 创建axios实例
const service = axios.create({
  baseURL: config.baseURL,
  timeout: config.timeout,
  headers: config.headers
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 在发送请求之前做些什么
    // 可以从localStorage获取token并添加到请求头
    const token = localStorage.getItem('token')
    if (token) {
      // 后端通常要求 Authorization: Bearer <token>
      const hasBearer = /^Bearer\s+/i.test(token)
      const value = hasBearer ? token : `Bearer ${token}`
      // 统一使用首字母大写的 Authorization 以兼容大多数网关
      config.headers['Authorization'] = value
    }
    return config
  },
  error => {
    // 对请求错误做些什么
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    // 如果是文件下载（blob 类型），检查状态码
    if (response.config.responseType === 'blob') {
      // 检查 HTTP 状态码，如果不是 2xx，说明请求失败
      if (response.status < 200 || response.status >= 300) {
        const error = new Error(`下载失败，状态码: ${response.status}`);
        error.response = response;
        error.status = response.status;
        return Promise.reject(error);
      }
      
      // 检查是否是错误响应（错误响应可能是 JSON 格式的 blob）
      if (response.data && response.data.type && response.data.type.includes('application/json')) {
        // 如果是 JSON blob，说明可能是错误信息，需要读取并解析
        return response.data.text().then(text => {
          try {
            const errorData = JSON.parse(text);
            const error = new Error(errorData.msg || errorData.message || '下载失败');
            error.response = response;
            error.code = errorData.code;
            return Promise.reject(error);
          } catch (e) {
            // 如果解析失败，返回原始 response
            return response;
          }
        });
      }
      // 如果是正常的文件 blob，返回完整的 response 对象（包含 headers）
      return response;
    }
    
    // 对响应数据做点什么
    const res = response.data
    
    // 根据实际的后端响应结构来判断请求是否成功
    // 这里假设后端返回的格式为 { code: 200, data: ..., message: ... }
    if (res.code && res.code !== 200) {
      // 如果code不是200，说明请求失败
      const errorMsg = res.msg || res.message || '请求失败';
      console.error('请求失败:', errorMsg);
      // 创建一个包含完整响应数据的错误对象
      const error = new Error(errorMsg);
      error.response = response; // 保留完整的响应对象
      error.code = res.code; // 保留错误码
      return Promise.reject(error);
    }
    
    return res
  },
  error => {
    // 对响应错误做点什么
    console.error('响应错误:', error)
    
    // 处理文件下载的错误（blob 类型）
    if (error.config && error.config.responseType === 'blob' && error.response) {
      // 对于 blob 类型的错误响应，尝试读取错误信息
      if (error.response.data && error.response.data instanceof Blob) {
        // 检查是否是 JSON 格式的错误信息
        if (error.response.data.type && error.response.data.type.includes('application/json')) {
          return error.response.data.text().then(text => {
            try {
              const errorData = JSON.parse(text);
              const downloadError = new Error(errorData.msg || errorData.message || '下载失败');
              downloadError.response = error.response;
              downloadError.status = error.response.status;
              return Promise.reject(downloadError);
            } catch (e) {
              // 如果解析失败，使用状态码信息
              const statusError = new Error(`下载失败，状态码: ${error.response.status}`);
              statusError.response = error.response;
              statusError.status = error.response.status;
              return Promise.reject(statusError);
            }
          });
        }
      }
      // 如果不是 JSON，直接使用状态码信息
      const statusError = new Error(`下载失败，状态码: ${error.response.status}`);
      statusError.response = error.response;
      statusError.status = error.response.status;
      return Promise.reject(statusError);
    }
    
    // 处理常见的HTTP错误状态码
    if (error.response) {
      switch (error.response.status) {
        case 400: {
          const errorMsg = error.response.data?.msg || error.response.data?.message || '请求参数错误';
          const badRequestError = new Error(errorMsg);
          badRequestError.response = error.response;
          badRequestError.status = 400;
          return Promise.reject(badRequestError);
        }
        case 401: {
          console.error('未授权，请重新登录 (401)')
          // 清除token和登录状态
          localStorage.removeItem('token')
          localStorage.removeItem('isLoggedIn')
          // 触发登录状态更新事件
          if (window.updateQintuLoginStatus) {
            window.updateQintuLoginStatus(false)
          }
          // 如果是浏览器环境，可以触发自定义事件
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new StorageEvent('storage', {
              key: 'isLoggedIn',
              newValue: 'false',
              oldValue: 'true'
            }))
          }
          // 确保错误被正确抛出，包含 401 状态码信息
          const unauthorizedError = new Error(error.response.data?.msg || error.response.data?.message || '未授权，请重新登录')
          unauthorizedError.response = error.response
          unauthorizedError.status = 401
          return Promise.reject(unauthorizedError)
        }
        case 403:
          console.error('拒绝访问')
          break
        case 404:
          console.error('请求的资源不存在')
          break
        case 500: {
          const errorMsg = error.response.data?.msg || error.response.data?.message || '服务器内部错误';
          const serverError = new Error(errorMsg);
          serverError.response = error.response;
          serverError.status = 500;
          return Promise.reject(serverError);
        }
        default:
          console.error('请求失败:', error.response.status)
      }
    }
    
    return Promise.reject(error)
  }
)

export default service

